import { Component } from "react";
import { Button, Card, Container, Spinner } from "react-bootstrap";
import { EmbeddedEmulator } from "../components/EmbeddedEmulator";
import GameData from "../mobx/GameData";
import slugify from 'slugify';

import "./GamePlayPage.scss";
import { CONSTANTS } from "../config/Constants";

export default class GamePlayPage extends Component {

    gameId = null;
    gameTitle = null;
    mdDescription = 'loading...';

    constructor(props) {
        super(props);

        this.state = {
            error: false,
            gameData: {},
            loading: true
        };

        // Since the creators of react router can't be arsed to make something useful and want everyone
        // to switch to function components rather than writing a simple wrapper for those of us still using
        // class components, ditch it entirely and get the params from the url. Next time, I'm going to start with wouter.
        // react-router has given me nothing but trouble, since they insist on changing their apis constantly.
        const pathSplit = window.location.pathname.split('/');
        if (pathSplit.length < 3) {
            // Failed
            this.state.loading = false;
            this.state.error = true;
            return;
        }
        this.gameId = pathSplit[2];
        this.gameTitle = pathSplit[3];
    }

    componentDidMount() {
        this.initialize();
    }

    async initialize() {
        if (this.gameId === 'preview' && this.gameTitle === 'current') {

            try {
                await new Promise(resolve => this.setState({
                    gameData: JSON.parse(localStorage.getItem('RetroPuzzleMakerV2.CurrentGamePreview'))
                }, resolve));
            } catch (e) {
                this.setState({loading: false, error: true});
                console.error('Failed loading game', e);
                return;
            }
        } else {
            try { 
                const datJson = await fetch(`${CONSTANTS.GameS3Url}${this.gameId}.data`),
                    dat = await datJson.text(),
                    datb = atob(dat),
                    obj = JSON.parse(datb);

                this.setState({
                    gameData: obj,
                })
            } catch (e) {
                this.setState({loading: false, error: true});
                console.error('Failed loading game', e);
            }
        }

        this.refreshMarkdown();
        await this.buildCredits();
        this.setState({loading: false});
    }

    refreshMarkdown() {
        if (this.state.gameData && this.state.gameData.gameDescription) {
            this.mdDescription = window.markdownit().render(this.state.gameData.gameDescription);
        } else {
            this.mdDescription = '';
        }
    }

    async buildCredits() {
        const BuiltInSongs = (await import('../data/BuiltInSongs')).default;
        const BuiltInTilesets = (await import ('../data/BuiltInTilesets')).default;
        const BuiltInSprites = (await import ('../data/BuiltInSprites')).default;

        if (this.state.gameData.useBuiltInGraphics) {
            this.tileInfo = {purpose: 'Game Tiles', ...BuiltInTilesets.find(a => a.id === this.state.gameData.builtInTileId)};
            this.spriteInfo = {purpose: 'Game Sprites', ...BuiltInSprites.find(a => a.id === this.state.gameData.builtInTileId)};
        }

        this.musicInfo = [];
        this.musicInfo.push({purpose: 'Title Music', ...BuiltInSongs.find(a => a.id === this.state.gameData.titleSong)});

        if (this.state.gameData.gameplaySong !== this.state.gameData.titleSong) {
            this.musicInfo.push({purpose: 'Gameplay Music', ...BuiltInSongs.find(a => a.id === this.state.gameData.gameplaySong)});
        }

        if (this.state.gameData.creditsSong !== this.state.gameData.gameplaySong && this.state.gameData.creditsSong !== this.state.gameData.titleSong) {
            this.musicInfo.push({purpose: 'Credits Music', ...BuiltInSongs.find(a => a.id === this.state.gameData.creditsSong)});
        }

    }

    getCreditEntry(art) {
        return (
            <div className="credit-entry" key={art.id + ' ' + art.title}>
                <Card className="mb-3">
                    <Card.Body>
                        <Card.Title>{art.purpose}</Card.Title>

                        <h6><a rel="noreferrer" target="blank" href={art.link}>{art.title}</a></h6>
                        <table className="mt-1"><tbody>
                            <tr>
                                <td>Author: </td>
                                <td><a target="_blank" rel="noreferrer" href={art.authorLink}>{art.author}</a></td>
                            </tr>
                            <tr>
                                <td>Source: </td>
                                <td><a target="_blank" rel="noreferrer" href={art.sourceLink}>{art.source}</a></td>
                            </tr>
                            <tr>
                                <td>License: </td>
                                <td><a target="_blank" rel="noreferrer" href={art.licenseLink}>{art.license}</a></td>
                            </tr>
                        </tbody></table>
                    </Card.Body>
                </Card>
            </div>
        );

    }

    async downloadGame() {
        if (this.state.gameData.romDownloadAllowed) {
            const a = document.createElement('a');
            a.href = await GameData.getPatchedRomWithData(this.state.gameData);
            a.download = slugify(this.state.gameData.title) + '.nes';
            a.click();

            setTimeout(() => {
                URL.revokeObjectURL(a.href)
            }, 5000);
        } else {
            console.error('Rom download disallowed');
        }
    }    

    render() {
        let mainContent
        if (this.state.loading) {
            mainContent = (<>
                <h2>Loading Game Information...</h2>
                <div className="text-center">
                    <Spinner animation="grow" variant="primary" />
                </div>
            </>);
        } else if (this.state.error) {
            mainContent = <>
                <h2>Could not load game</h2>
                <p className="mt-3">Please try again, or contact the person that shared the link with you.</p>
                <p className="mt-3"><a href="/">Create your own game with Retro Puzzle Maker</a></p>
            </>
        } else {
            mainContent = (<>
                <h2>{this.state.gameData.title}</h2>
                <div className="emulator-container">
                    <EmbeddedEmulator game={this.state.gameData} />
                </div>
                {this.state.gameData.romDownloadAllowed ? (<div className="download-bar">
                    <Button variant="primary" onClick={() => this.downloadGame()}>Download NES Rom</Button>
                </div>) : (<> </>)}
                <div className="game-description mt-2" dangerouslySetInnerHTML={{__html: this.mdDescription}} />
                <div className="credits-area">
                    <h3 className="mt-3 mb-3">Art Credits</h3>
                    
                    {this.state.gameData.useBuiltInGraphics ? this.getCreditEntry(this.tileInfo) : (<></>)}
                    {this.state.gameData.useBuiltInGraphics ? this.getCreditEntry(this.spriteInfo) : (<></>)}

                    {this.musicInfo.map(music => this.getCreditEntry(music))}
                </div>

            </>)
        }

        return (
            <div className="game-play-page">
                <Container>
                    {mainContent}
                </Container>
            </div>
        )
    }
}